import type { ChargeBase, DepartureDto, PackageDto } from "@tripcart/shared";

import type { TravelersState } from "@/lib/cart/types";

/** Minimal category shape needed for pax (head-count) math. Satisfied by BOTH
 *  the API `PackageCategoryDto` and the storefront `PackageOption` category,
 *  so the booking widget, the package cards, and the cart all share ONE
 *  head-count definition instead of re-deriving it (the source of an oversell
 *  class of bugs). `unitCapacity` may be `null` (API) or `undefined`
 *  (storefront); `!= null` / `== null` treat both as "no bundling". */
export interface PaxCategory {
  id: string;
  chargeBase: ChargeBase;
  unitCapacity?: number | null;
}

// A `group` category with no `unitCapacity` — the shape this module used to
// call "flat" and count as ZERO travellers.
//
// It is NOT a fee with nobody on it. The operator form offers three "Charged
// as" options and none of them is a fee; per_group's own description is
// "Couple, private group". A row in this shape is a per_group category that
// LOST its seat count: `unit_fits` is nullable with no CHECK and no backfill,
// `resolveUnitFits` guards create and update but not the attach path, and the
// pre-2026-08 default templates seeded Couple with no seats at all. There are
// live rows, and `tests/e2e/scripts/seed-reference.ts` makes more on every run.
//
// So "unknown seats", never "no people".
function hasUnknownUnitSize(cat: PaxCategory): boolean {
  return cat.chargeBase === "group" && cat.unitCapacity == null;
}

/**
 * How many actual travellers a (category, count) tuple represents.
 *
 *   - `unitCapacity` set: count × unitCapacity. Three Couples (group, fits 2)
 *     are six people; two Cabins (unit, fits 4) are eight.
 *   - per-person: count.
 *   - a group whose seat count is missing: ONE head per step, never zero.
 *
 * That last rule is the whole point of this function agreeing with
 * `@tripcart/shared`'s `paxFor` (`count * (unitCapacity ?? 1)`) and
 * `paxHeadsFor`, whose own comment refuses zero for exactly this reason: "if a
 * corrupt row ever carried it, multiplying would report a line with people on
 * it as ZERO travellers."
 *
 * It returned 0, and the two definitions disagreed all the way to checkout.
 * `_packages-section.tsx` writes `categorySeatsPerUnit = paxFor(cat, 1)` onto
 * the cart line; `_traveler-slots.ts` and `_traveler-sync.ts` read it back as
 * `?? 1`, which does NOT fire for 0. So a traveller booking one of these rows
 * got ZERO traveller-detail forms and consumed ZERO seats against the
 * departure's capacity, while the server counted the same line as one head and
 * sold the seat. That is the oversell class this module's own header says it
 * exists to prevent.
 */
export function paxFor(cat: PaxCategory, count: number): number {
  // A rented item (kayak, jeep) is not a traveller and takes no seat.
  if (cat.chargeBase === "asset") return 0;
  if (cat.unitCapacity != null) return count * cat.unitCapacity;
  return count;
}

/** What ONE step of a category buys, as structured data the caller
 *  translates (this module stays i18n-free and unit-testable, same as the
 *  package booking gate).
 *
 *  - `fits`  — a per-unit category: one Cabin seats `seats` people.
 *  - `each`  — a per-group category: one Couple IS `seats` people.
 *  - `null`  — plain per-person; one step is one person and saying so is noise.
 *
 *  Without this the picker showed "Couple x 7" with no hint that the party is
 *  actually 14, while `sumPax` — and therefore the price, the capacity gate
 *  and the API — all counted 14. */
export type UnitHint =
  | { kind: "fits"; seats: number }
  | { kind: "each"; seats: number }
  | null;

export function describeUnit(cat: PaxCategory): UnitHint {
  // A group with unknown seats gets NO hint rather than the old "One fixed
  // fee". Saying a step carries nobody while `paxFor` counts it as one head
  // would put the two back in disagreement, in words this time.
  if (hasUnknownUnitSize(cat)) return null;
  if (cat.unitCapacity == null) return null;
  return cat.chargeBase === "unit"
    ? { kind: "fits", seats: cat.unitCapacity }
    : { kind: "each", seats: cat.unitCapacity };
}

/**
 * The fewest travellers this package can be sold to — one step of its
 * cheapest-to-seat category.
 *
 * A package sold only in Private Groups of 6 cannot be booked for fewer than
 * six people: the smallest thing the picker can add is one group. When that
 * number is larger than the seats left, NO party clears the date — the "+" is
 * refused at zero, and every message downstream that talks about choosing a
 * party size is advice the traveller cannot take.
 *
 * Measured with `paxFor`, so it answers the same question the PICKER answers.
 * A group row whose seat count was lost counts as ONE head here because that is
 * what `paxFor` counts it as, and therefore what `canIncrementCategory` lets a
 * traveller add: on a date with one seat left, a package sold as "Private Group
 * of 6" plus one such legacy row still has something addable, so it is not a
 * dead end and the card must not claim one. Excluding those rows — treating
 * "unknown size" as "cannot be sold" — would report the smallest party as 6 and
 * print a dead-end message the picker immediately contradicts by accepting the
 * legacy row. That disagreement is the class of bug this module's header exists
 * to prevent; `_booking-gate.group-unit.test.ts` pins it.
 *
 * `heads > 0` therefore drops only a row whose `unitCapacity` is literally 0,
 * not the `== null` shape — `paxFor` deliberately refuses to return zero for
 * that one ("unknown seats, never no people").
 */
export function smallestSellableParty(
  categories: ReadonlyArray<PaxCategory>,
): number | null {
  const steps = categories
    .map((cat) => paxFor(cat, 1))
    .filter((heads) => heads > 0);
  return steps.length === 0 ? null : Math.min(...steps);
}

// Sum head-count (not category-row count) across all categories. Used by the
// booking widget, the package cards, AND the cart, so every surface agrees on
// what "one Couple increment" means in seat terms.
export function sumPax(
  categories: ReadonlyArray<PaxCategory>,
  travelers: TravelersState,
): number {
  return categories.reduce(
    (sum, cat) => sum + paxFor(cat, travelers[cat.id] ?? 0),
    0,
  );
}

/**
 * Why one more step of `cat` will not fit, as structured data the caller
 * translates — this module stays i18n-free, same as `describeUnit`.
 * `null` means it fits.
 *
 * The picker already disabled "+" in this situation; what was missing was any
 * reason for it. A Couple needs 2 seats, so on a 1-seat cap its "+" is dead the
 * moment the picker opens and a traveller has no way to know why — it reads as
 * a broken button, not a full departure (QA ef4e80e0).
 *
 * Head-count, not row-count (invariant 2): one step of a per-unit/per-group
 * category buys `unitCapacity` heads, so it can be refused while a per-person
 * category on the same departure still fits.
 */
export function stepBlockedBy(args: {
  cat: PaxCategory;
  categories: ReadonlyArray<PaxCategory>;
  travelers: TravelersState;
  pickerCap: number | null;
}): { needs: number; seatsLeft: number } | null {
  const { cat, categories, travelers, pickerCap } = args;
  // Nothing bounds the picker — an uncapped departure never blocks a step.
  if (pickerCap === null) return null;
  const needs = paxFor(cat, 1);
  const seatsLeft = pickerCap - sumPax(categories, travelers);
  // No early return for a flat group fee (`needs === 0`). It carries no
  // travellers, so `0 <= seatsLeft` leaves it unblocked in every ordinary case
  // — but when the party ALREADY exceeds the cap (the departure shrank under a
  // party that was legal when it was picked) the reducer rejects its "+" too,
  // and bailing out here left that one row with a dead button and no
  // explanation: the exact defect this function exists to remove, surviving on
  // the one row type it skipped. Falling through mirrors the reducer, and the
  // caller phrases `needs === 0` differently because "needs 0 seats" is not a
  // sentence.
  if (needs <= seatsLeft) return null;
  // Clamped: an over-committed departure would otherwise report "-3 left".
  return { needs, seatsLeft: Math.max(0, seatsLeft) };
}

// Global picker cap: the largest party any upcoming, non-closed departure in
// the window can accommodate by *total capacity* (the operator's configured
// ceiling), NOT by remaining seats. The traveller may express a party up to
// the full capacity even when seats are already booked; the per-date package
// card then validates the party against that date's remaining `spotsLeft` and
// the backend enforces the hard limit at booking time.
//
// The cap comes from `DepartureDto.totalCapacity` — the date's ceiling BEFORE
// any package allocation narrows it — and deliberately NOT from
// `DepartureDto.capacity`, which is that same ceiling scoped to one package.
// On a 10-seat departure split 6/4 between two packages, sizing the picker
// from `capacity` caps the party at 6 and no package ever gets the chance to
// say whether it can take 10. With the total, the picker reaches 10, the
// package that cannot seat the party reports "only N spots left, select N or
// fewer" (`resolvePackageBookingGate`), and the one that can stays bookable.
//
// We take the MAX across open departures so the picker can reach the largest
// seasonal `capacityOverride` in the window (e.g. default 20, Season 1 cap
// 25 → picker must reach 25). `totalCapacity` is package-independent, so
// every package's row on a date carries the same number and no per-package
// filtering applies.
//
// Legacy responses omit `totalCapacity` entirely; those fall back to the
// previous rule (max `capacity` across the ACTIVE package's rows), which is
// the best the older wire format supports. `pkg.capacity` is the last resort,
// before departures load or when every open date is closed. Returns `null`
// when nothing bounds the picker (an uncapped departure → unbounded).
export function resolveGlobalCapacityCap(
  pkg: PackageDto | null,
  departures: DepartureDto[] | null,
): number | null {
  const open = (departures ?? []).filter((d) => !d.isClosed);

  const total = scanMaxCapacity(open, (d) => d.totalCapacity);
  if (total.bounded) return total.cap;

  const perPackage = scanMaxCapacity(open, (d) =>
    pkg?.id && d.packageId && d.packageId !== pkg.id ? undefined : d.capacity,
  );
  if (perPackage.bounded) return perPackage.cap;

  return pkg?.capacity ?? null;
}

/** Largest `valueOf` across the rows, where `undefined` means "this row does
 *  not carry the value" (skip it) and `null` means uncapped — which wins
 *  outright, since one unbounded departure leaves the picker unbounded.
 *  `bounded: false` means no row contributed and the caller should fall
 *  through to its next source. */
function scanMaxCapacity(
  departures: DepartureDto[],
  valueOf: (d: DepartureDto) => number | null | undefined,
): { bounded: boolean; cap: number | null } {
  let max = 0;
  for (const d of departures) {
    const value = valueOf(d);
    if (value === undefined) continue;
    if (value === null) return { bounded: true, cap: null };
    if (value > max) max = value;
  }
  return { bounded: max > 0, cap: max };
}

/** What ONE step of a category buys, phrased as a rate basis — "per person",
 *  "per unit", "per group", or the operator's own wording where they wrote
 *  some. Never the category NAME: `mapApiCategory` sets
 *  `displayLabel: cat.displayLabel || cat.name`, so an operator who left the
 *  phrasing blank would otherwise turn this into the name, and the line reads
 *  "Rs1,350.00 Adult" — a rate per Adult-the-noun.
 *
 *  Lives here, beside `paxFor`, because the rail and the package card both
 *  need it and a verbatim copy in each is how the two rules around it already
 *  drifted apart. Same reason this module owns head-counting.
 *
 *  The name comparison is trimmed and case-folded. It was an exact `!==`, which
 *  only catches a label that matches the name character for character — the
 *  operator form neither trims nor case-normalises this field and the API
 *  passes it through verbatim, so a name of "Adult" with a label of "adult" or
 *  "Adult " walked straight past the guard and printed the name anyway.
 *  `PriceCategoryTable` in operator-web already answers this question the same
 *  way. */
export function chargeBasisLabel(
  // Structural, not `PackageCategory`: the sidebar's own category carries more
  // (strategy, minPax, ageGroup) and only these three decide the phrasing.
  cat: {
    name: string;
    displayLabel: string;
    chargeBase: ChargeBase;
  },
  t: (key: string) => string,
): string {
  const label = cat.displayLabel.trim();
  if (label && label.toLowerCase() !== cat.name.trim().toLowerCase())
    return label;
  // Exhaustive over the wire enum (`chargeBaseValues`), so
  // there is no fourth arm and no null return — a caller needs no fallback.
  switch (cat.chargeBase) {
    case "person":
      return t("per person");
    case "unit":
      return t("per unit");
    case "group":
      return t("per group");
    case "asset":
      return t("per item");
  }
}
